import React, { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { usePalette } from '../../App'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ToolNavItem {
  label: string
  to:    string
}

interface CategoryConfig {
  id:          string
  label:       string
  icon:        React.ReactNode
  tools:       ToolNavItem[]
  comingSoon?: boolean
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SidebarProps {
  /** Called when a nav link is activated (e.g. to close mobile drawer). */
  onNavigate?: () => void
}

// ── Category icons (inline SVG, Lucide-style paths) ───────────────────────────

const FormatterIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 3H7a2 2 0 00-2 2v5a2 2 0 01-2 2 2 2 0 012 2v5c0 1.1.9 2 2 2h1M16 21h1a2 2 0 002-2v-5a2 2 0 012-2 2 2 0 01-2-2V5a2 2 0 00-2-2h-1" />
  </svg>
)

const EncoderIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
)

const GeneratorIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74L12 2z" />
  </svg>
)

const ConverterIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4M4 17h12m0 0l-4-4m4 4l-4 4" />
  </svg>
)

const ValidatorIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const NetworkIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9" />
  </svg>
)

const ApiLabIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
  </svg>
)

const SignInIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
  </svg>
)

const SignOutIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
)

// ── Category taxonomy ─────────────────────────────────────────────────────────
// Only currently available tools are listed; Week 2 additions will expand each category.

const CATEGORIES: CategoryConfig[] = [
  {
    id: 'formatters',
    label: 'Formatters',
    icon: <FormatterIcon />,
    tools: [
      { label: 'JSON Formatter',    to: '/tools/json-formatter' },
      { label: 'HTML Formatter',    to: '/tools/html-formatter' },
      { label: 'SQL Formatter',     to: '/tools/sql-formatter' },
      { label: 'Markdown Preview',  to: '/tools/markdown-preview' },
      { label: 'YAML ↔ JSON',       to: '/tools/yaml-json' },
    ],
  },
  {
    id: 'encoders',
    label: 'Encoders / Decoders',
    icon: <EncoderIcon />,
    tools: [
      { label: 'Base64 Codec',        to: '/tools/base64' },
      { label: 'URL Encoder/Decoder', to: '/tools/url-encoder' },
      { label: 'HTML Entity Encoder', to: '/tools/html-entities' },
      { label: 'JWT Decoder',         to: '/tools/jwt-decoder' },
      { label: 'Image → Base64',      to: '/tools/image-base64' },
    ],
  },
  {
    id: 'generators',
    label: 'Generators',
    icon: <GeneratorIcon />,
    tools: [
      { label: 'UUID Generator',      to: '/tools/uuid-generator' },
      { label: 'Password Generator',  to: '/tools/password-generator' },
      { label: 'Hash Generator',      to: '/tools/hash-generator' },
      { label: 'Lorem Ipsum',         to: '/tools/lorem-ipsum' },
      { label: 'QR Code Generator',   to: '/tools/qr-generator' },
      { label: 'JWT Generator',       to: '/tools/jwt-generator' },
      { label: 'HMAC Generator',      to: '/tools/hmac-generator' },
      { label: 'File Hash',           to: '/tools/file-hash' },
      { label: 'Image Compressor',    to: '/tools/image-compress' },
    ],
  },
  {
    id: 'converters',
    label: 'Converters',
    icon: <ConverterIcon />,
    tools: [
      { label: 'Timestamp Converter',      to: '/tools/timestamp' },
      { label: 'Color Converter',          to: '/tools/color-converter' },
      { label: 'Number Base Converter',    to: '/tools/number-base' },
      { label: 'String Case Converter',    to: '/tools/string-case' },
      { label: 'CSV ↔ JSON',              to: '/tools/csv-json' },
      { label: 'Text Diff Viewer',         to: '/tools/text-diff' },
      { label: 'curl → Code',             to: '/tools/curl-converter' },
      { label: 'TOML ↔ JSON',            to: '/tools/toml-json' },
      { label: 'SQL Playground',           to: '/tools/sql-playground' },
      { label: 'SVG Optimizer',            to: '/tools/svg-optimizer' },
      { label: 'CSV Large File',           to: '/tools/csv-large' },
    ],
  },
  {
    id: 'validators',
    label: 'Validators',
    icon: <ValidatorIcon />,
    tools: [
      { label: 'Regex Tester',          to: '/tools/regex-tester' },
      { label: 'Cron Parser',           to: '/tools/cron-parser' },
      { label: 'HTTP Status Codes',     to: '/tools/http-status' },
      { label: 'MIME Type Lookup',      to: '/tools/mime-lookup' },
      { label: 'JSON → TypeScript',     to: '/tools/json-to-ts' },
      { label: 'Image EXIF Viewer',     to: '/tools/image-exif' },
      { label: 'Python Runner',          to: '/tools/python-runner' },
      { label: 'PDF Text Extractor',     to: '/tools/pdf-extractor' },
    ],
  },
  {
    id: 'network',
    label: 'Network Tools',
    icon: <NetworkIcon />,
    tools: [
      { label: 'DNS Lookup',             to: '/tools/dns-lookup' },
      { label: 'SSL Inspector',          to: '/tools/ssl-inspector' },
      { label: 'HTTP Headers Inspector', to: '/tools/http-headers' },
      { label: 'IP Info Lookup',         to: '/tools/ip-info' },
      { label: 'URL Metadata Inspector', to: '/tools/url-metadata' },
      { label: 'Webhook Tester',         to: '/tools/webhook-tester' },
      ...(import.meta.env.VITE_SCREENSHOT_ENABLED === 'true'
        ? [{ label: 'Screenshot Tool', to: '/tools/screenshot' }]
        : []),
    ],
  },
]

// ── localStorage helpers ───────────────────────────────────────────────────────

const OPEN_KEY = 'devhub:sidebar:open'

function readOpenCategory(): string | null {
  try { return localStorage.getItem(OPEN_KEY) }
  catch { return null }
}

function saveOpenCategory(id: string | null): void {
  try {
    if (id) localStorage.setItem(OPEN_KEY, id)
    else localStorage.removeItem(OPEN_KEY)
  } catch { /* ignore */ }
}

// ── Sidebar component ─────────────────────────────────────────────────────────

const Sidebar: React.FC<SidebarProps> = ({ onNavigate }) => {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate  = useNavigate()
  const location  = useLocation()

  // Detect tablet compact mode (768–1023 px)
  const [isCompact, setIsCompact] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px) and (max-width: 1023px)')
    setIsCompact(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Which category contains the active route
  const activeCategoryId = useMemo<string | null>(() => {
    for (const cat of CATEGORIES) {
      if (cat.tools.some(t => location.pathname === t.to)) return cat.id
    }
    return null
  }, [location.pathname])

  // Open category — initialized from localStorage, falls back to active category
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(() =>
    readOpenCategory() ?? activeCategoryId,
  )

  // Auto-expand the parent category whenever the active route changes
  const prevActiveRef = useRef<string | null>(null)
  useEffect(() => {
    if (activeCategoryId && activeCategoryId !== prevActiveRef.current) {
      prevActiveRef.current = activeCategoryId
      setOpenCategoryId(activeCategoryId)
      saveOpenCategory(activeCategoryId)
    }
  }, [activeCategoryId])

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
    onNavigate?.()
  }

  const toggleCategory = (id: string) => {
    const next = openCategoryId === id ? null : id
    setOpenCategoryId(next)
    saveOpenCategory(next)
  }

  // ── Compact layout (tablet 768–1023 px) ─────────────────────────────────────
  if (isCompact) {
    return (
      <nav className="flex flex-col items-center h-full py-3 gap-1 select-none overflow-hidden" aria-label="Tool navigation">
        {/* Brand icon */}
        <Link to="/" onClick={onNavigate} className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-600 flex-shrink-0 mb-2 hover:bg-primary-500 transition-colors" title="Home">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16M4 9h16M4 15h16" />
          </svg>
        </Link>

        {/* Batch Processing icon */}
        <NavLink
          to="/tools/batch"
          title="Batch Processing"
          onClick={onNavigate}
          className={({ isActive }) => [
            'flex items-center justify-center w-9 h-9 rounded-md transition-colors duration-fast text-xs font-bold',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isActive
              ? 'bg-primary-600/20 text-primary-300'
              : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100',
          ].join(' ')}
        >
          ⚡
        </NavLink>

        {/* API Lab icon */}
        <NavLink
          to="/lab"
          title="API Lab"
          onClick={onNavigate}
          className={({ isActive }) => [
            'flex items-center justify-center w-9 h-9 rounded-md transition-colors duration-fast',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isActive
              ? 'bg-primary-600/20 text-primary-300'
              : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100',
          ].join(' ')}
        >
          <ApiLabIcon />
        </NavLink>

        {/* Category icons — navigate to first available tool in category */}
        {CATEGORIES.map(cat => {
          const firstTool = cat.tools[0]
          const isActive  = activeCategoryId === cat.id

          if (cat.comingSoon || !firstTool) {
            return (
              <div
                key={cat.id}
                title={`${cat.label} — Coming Soon`}
                className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-600 cursor-default"
              >
                {cat.icon}
              </div>
            )
          }

          return (
            <NavLink
              key={cat.id}
              to={firstTool.to}
              title={cat.label}
              onClick={onNavigate}
              className={[
                'flex items-center justify-center w-9 h-9 rounded-md transition-colors duration-fast',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                isActive
                  ? 'bg-primary-600/20 text-primary-300'
                  : 'text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100',
              ].join(' ')}
            >
              {cat.icon}
            </NavLink>
          )
        })}

        {/* Auth icons at bottom */}
        <div className="mt-auto flex flex-col items-center gap-1 pb-2">
          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogout}
              title="Sign out"
              className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              <SignOutIcon />
            </button>
          ) : (
            <NavLink
              to="/login"
              title="Sign in"
              onClick={onNavigate}
              className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              <SignInIcon />
            </NavLink>
          )}
        </div>
      </nav>
    )
  }

  // ── Full layout (desktop ≥1024 px and mobile drawer) ─────────────────────────
  const { openPalette } = usePalette()
  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac')

  return (
    <nav className="flex flex-col h-full w-full select-none" aria-label="Tool navigation">

      {/* ── Brand / logo area ─────────────────────────────────────────────── */}
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2.5 px-4 py-5 border-b border-neutral-700/60 hover:bg-neutral-800/40 transition-colors">
        <div className="flex items-center justify-center w-7 h-7 rounded-md bg-primary-600 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16M4 9h16M4 15h16" />
          </svg>
        </div>
        <div className="flex flex-col leading-none">
          <span className="text-sm font-bold tracking-tight text-neutral-100">DevHub</span>
          <span className="text-2xs font-medium text-neutral-500 uppercase tracking-widest">Tools Platform</span>
        </div>
      </Link>

      {/* ── Batch Processing — always-visible top-level entry ─────────────── */}
      <div className="px-2 pb-1">
        <NavLink
          to="/tools/batch"
          onClick={onNavigate}
          className={({ isActive }) => [
            'group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium',
            'transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isActive
              ? 'bg-primary-600/20 text-primary-300 border border-primary-600/30'
              : 'text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100',
          ].join(' ')}
        >
          <span className="truncate">Batch Processing</span>
        </NavLink>
      </div>

      {/* ── API Lab — always-visible top-level entry ───────────────────────── */}
      <div className="px-2 pt-3 pb-2">
        <NavLink
          to="/lab"
          onClick={onNavigate}
          className={({ isActive }) => [
            'group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium',
            'transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
            isActive
              ? 'bg-primary-600/20 text-primary-300 border border-primary-600/30'
              : 'text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100',
          ].join(' ')}
        >
          {({ isActive }) => (
            <>
              <span
                className={['flex-shrink-0', isActive ? 'text-primary-400' : 'text-neutral-500 group-hover:text-neutral-300'].join(' ')}
                aria-hidden="true"
              >
                <ApiLabIcon />
              </span>
              <span className="truncate">API Lab</span>
              {isActive && (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary-400 flex-shrink-0" aria-hidden="true" />
              )}
            </>
          )}
        </NavLink>
      </div>

      {/* ── SidebarSearch — visible search launcher ────────────────────────── */}
      <div className="px-2 pb-2">
        <button
          type="button"
          onClick={() => openPalette('')}
          className="w-full flex items-center gap-2 h-8 px-3 rounded-md border border-neutral-700 bg-neutral-800 text-left transition-colors hover:border-neutral-600 hover:bg-neutral-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          {/* Magnifying glass icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 text-neutral-500 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <span className="flex-1 text-xs text-neutral-500 select-none">Search tools...</span>
          <kbd className="inline-flex items-center gap-0.5 rounded border border-neutral-700 bg-neutral-900 px-1 py-0.5 text-[10px] font-mono text-neutral-500 flex-shrink-0 not-italic">
            {isMac ? '⌘K' : 'Ctrl K'}
          </kbd>
        </button>
      </div>

      {/* ── Tools section label ────────────────────────────────────────────── */}
      <div className="px-4 pb-1.5">
        <span className="text-2xs font-semibold uppercase tracking-widest text-neutral-500">
          Tools
        </span>
      </div>

      {/* ── Category accordion ────────────────────────────────────────────── */}
      <ul className="flex flex-col gap-0.5 px-2 flex-1 overflow-y-auto" role="list">
        {CATEGORIES.map(cat => {
          const isOpen      = openCategoryId === cat.id
          const isCatActive = activeCategoryId === cat.id

          return (
            <li key={cat.id}>
              {/* Category header */}
              <button
                type="button"
                onClick={() => { if (!cat.comingSoon) toggleCategory(cat.id) }}
                disabled={cat.comingSoon}
                title={cat.comingSoon ? `${cat.label} — Coming Soon` : cat.label}
                className={[
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-md',
                  'text-xs font-semibold uppercase tracking-widest',
                  'transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                  cat.comingSoon
                    ? 'text-neutral-600 cursor-default'
                    : isCatActive
                    ? 'text-neutral-200 hover:bg-neutral-800/60'
                    : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800/40',
                ].join(' ')}
              >
                {/* Icon */}
                <span
                  className={cat.comingSoon ? 'text-neutral-600' : isCatActive ? 'text-primary-400' : 'text-neutral-500'}
                  aria-hidden="true"
                >
                  {cat.icon}
                </span>

                {/* Label */}
                <span className="flex-1 text-left">{cat.label}</span>

                {/* Coming Soon badge */}
                {cat.comingSoon && (
                  <span className="text-[10px] font-medium normal-case tracking-normal text-neutral-600 bg-neutral-800 border border-neutral-700/60 px-1.5 py-0.5 rounded">
                    Soon
                  </span>
                )}

                {/* Chevron */}
                {!cat.comingSoon && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={['h-3 w-3 flex-shrink-0 transition-transform duration-fast', isOpen ? 'rotate-180' : ''].join(' ')}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </button>

              {/* Tool list — visible when open */}
              {!cat.comingSoon && isOpen && (
                <ul className="pl-3 pb-1 flex flex-col gap-0.5" role="list">
                  {cat.tools.length === 0 ? (
                    <li>
                      <span className="block px-3 py-1.5 text-xs text-neutral-600 italic">
                        More tools coming soon
                      </span>
                    </li>
                  ) : (
                    cat.tools.map(tool => (
                      <li key={tool.to}>
                        <NavLink
                          to={tool.to}
                          onClick={onNavigate}
                          className={({ isActive }) => [
                            'flex items-center h-8 px-3 text-sm rounded-md',
                            'transition-colors duration-fast focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                            isActive
                              ? 'bg-neutral-700 text-primary-300'
                              : 'text-neutral-300 hover:bg-neutral-700/60',
                          ].join(' ')}
                        >
                          <span className="truncate">{tool.label}</span>
                        </NavLink>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </li>
          )
        })}
      </ul>

      {/* ── Auth section ──────────────────────────────────────────────────── */}
      <div className="px-3 pb-2 mt-auto border-t border-neutral-700/60 pt-3">
        {isAuthenticated && user ? (
          <>
            {/* Plan badge + email */}
            <div className="flex items-center gap-2 px-2 mb-2">
              <span
                className={[
                  'inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-widest',
                  user.plan === 'pro'
                    ? 'bg-primary-900/60 text-primary-300 border border-primary-700/50'
                    : 'bg-neutral-800 text-neutral-400 border border-neutral-700',
                ].join(' ')}
              >
                {user.plan === 'pro' ? 'Pro' : 'Free'}
              </span>
              <span className="truncate text-xs text-neutral-500">{user.email}</span>
            </div>
            {/* Pro-only links */}
            {user.plan === 'pro' && (
              <>
                <Link
                  to="/history"
                  onClick={onNavigate}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
                >
                  History
                </Link>
                <Link
                  to="/files"
                  onClick={onNavigate}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
                >
                  My Files
                </Link>
                <Link
                  to="/settings/api-keys"
                  onClick={onNavigate}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
                >
                  API Keys
                </Link>
              </>
            )}
            {/* Billing link */}
            <Link
              to="/billing"
              onClick={onNavigate}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              Billing
            </Link>
            {/* Logout */}
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              Sign out
            </button>
          </>
        ) : (
          <>
            <Link
              to="/login"
              onClick={onNavigate}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              onClick={onNavigate}
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-neutral-400 border border-transparent hover:bg-neutral-800 hover:text-neutral-100 transition-colors duration-fast"
            >
              Create account
            </Link>
          </>
        )}
      </div>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <div className="px-4 py-3 border-t border-neutral-700/60">
        <p className="text-2xs text-neutral-600 font-mono">v1.0.0 — DevHub</p>
      </div>
    </nav>
  )
}

export default Sidebar
